import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Carousel.scss';

const AUTOPLAY_MS = 5000;

const isVideo = (src) => typeof src === 'string' && src.toLowerCase().endsWith('.mp4');

const MyCarousel = ({ pictures, getFallback }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const videoRefs = useRef({});
  const progressRef = useRef(null);
  const thumbsRef = useRef(null);
  const dragStart = useRef(null);
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const count = pictures.length;
  const goTo = useCallback((i) => {
    if (!count) return;
    setSelectedIndex(((i % count) + count) % count);
  }, [count]);
  const nextSlide = useCallback(() => goTo(selectedIndex + 1), [goTo, selectedIndex]);
  const prevSlide = useCallback(() => goTo(selectedIndex - 1), [goTo, selectedIndex]);

  // Si la liste change (chargement API), on repart au début.
  useEffect(() => { setSelectedIndex(0); }, [pictures]);

  // Met en pause les vidéos qui ne sont plus affichées.
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([index, videoEl]) => {
      if (Number(index) !== selectedIndex && videoEl && !videoEl.paused) {
        videoEl.pause();
      }
    });
  }, [selectedIndex]);

  useEffect(() => {
    const onVisibility = () => setIsHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const autoplay = count > 1 && !reducedMotion && !isHovered && !isVideoPlaying && !isHidden;

  // Lecture automatique : un compte à rebours par photo, relancé à chaque changement.
  useEffect(() => {
    if (!autoplay) return undefined;
    const timer = setTimeout(() => goTo(selectedIndex + 1), AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [autoplay, selectedIndex, goTo]);

  // Sur mobile, les miniatures défilent : on garde la miniature active visible.
  useEffect(() => {
    const row = thumbsRef.current;
    if (!row || row.scrollWidth <= row.clientWidth) return;
    const el = row.children[selectedIndex];
    if (!el) return;
    const left = el.offsetLeft - (row.clientWidth - el.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [selectedIndex]);

  // Barre de progression synchronisée avec le compte à rebours.
  useEffect(() => {
    const bar = progressRef.current;
    if (!bar) return;
    bar.classList.remove('carousel-progress__bar--run');
    bar.style.width = '0';
    if (!autoplay) return;
    void bar.offsetWidth; // force le navigateur à prendre en compte la largeur 0
    bar.style.width = '';
    bar.classList.add('carousel-progress__bar--run');
  }, [autoplay, selectedIndex]);

  const onPointerDown = (e) => {
    dragStart.current = e.clientX;
  };
  const onPointerUp = (e) => {
    if (dragStart.current === null) return;
    const dx = e.clientX - dragStart.current;
    dragStart.current = null;
    if (dx < -40) nextSlide();
    else if (dx > 40) prevSlide();
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') prevSlide();
    if (e.key === 'ArrowRight') nextSlide();
  };

  if (!count) return null;

  return (
    <div
      className='carousel'
      tabIndex={0}
      aria-roledescription='carrousel'
      aria-label='Photos de la boutique'
      onKeyDown={onKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <div
        className='carousel-viewport'
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { dragStart.current = null; }}
      >
        <div className='carousel-track' style={{ transform: `translateX(${-selectedIndex * 100}%)` }}>
          {pictures.map((picture, index) => (
            <figure
              key={index}
              className={`carousel-slide ${index === selectedIndex ? 'active' : ''}`}
              aria-hidden={index !== selectedIndex}
            >
              {isVideo(picture) ? (
                <div className="carousel-video-wrapper">
                  <video
                    ref={(el) => { videoRefs.current[index] = el; }}
                    src={picture}
                    controls
                    loop
                    muted={isMuted}
                    playsInline
                    onPlay={() => setIsVideoPlaying(true)}
                    onPause={() => setIsVideoPlaying(false)}
                    aria-label={`Vidéo BAsique ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="carousel-play-btn"
                    onClick={() => {
                      const videoEl = videoRefs.current[index];
                      if (!videoEl) return;
                      if (videoEl.paused) videoEl.play();
                      else videoEl.pause();
                    }}
                    aria-label={isVideoPlaying ? 'Mettre en pause' : 'Lancer la vidéo'}
                  >
                    {isVideoPlaying ? (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="5" width="4" height="14"></rect>
                        <rect x="14" y="5" width="4" height="14"></rect>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="6 4 20 12 6 20 6 4"></polygon>
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="carousel-mute-btn"
                    onClick={() => setIsMuted((prev) => !prev)}
                    aria-label={isMuted ? 'Activer le son' : 'Couper le son'}
                  >
                    {isMuted ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                        <line x1="23" y1="9" x2="17" y2="15"></line>
                        <line x1="17" y1="9" x2="23" y2="15"></line>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                      </svg>
                    )}
                  </button>
                </div>
              ) : (
                <img
                  src={picture}
                  alt={`Magasin BAsique ${index + 1}`}
                  draggable={false}
                  onError={(e) => {
                    if (!getFallback || e.target.dataset.fallbackApplied) return;
                    e.target.dataset.fallbackApplied = 'true';
                    e.target.src = getFallback(picture, index);
                  }}
                />
              )}
            </figure>
          ))}
        </div>

        {count > 1 && (
          <>
            <button className="carousel-arrow carousel-arrow--left" type="button" onClick={prevSlide} aria-label="Photo précédente">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button className="carousel-arrow carousel-arrow--right" type="button" onClick={nextSlide} aria-label="Photo suivante">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
            </button>
            <div className="carousel-progress" aria-hidden="true"><i ref={progressRef} className="carousel-progress__bar"></i></div>
          </>
        )}
      </div>

      {count > 1 && (
        <div className='carousel-thumbs' role='tablist' aria-label='Choisir une photo' ref={thumbsRef}>
          {pictures.map((picture, index) => (
            <button
              key={index}
              type='button'
              role='tab'
              aria-selected={index === selectedIndex}
              aria-label={`Photo ${index + 1}`}
              className={`carousel-thumb ${index === selectedIndex ? 'active' : ''}`}
              onClick={() => goTo(index)}
            >
              {isVideo(picture) ? (
                <span className='carousel-thumb__video'>
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="6 4 20 12 6 20 6 4"></polygon></svg>
                </span>
              ) : (
                <img
                  src={picture}
                  alt=''
                  draggable={false}
                  onError={(e) => {
                    if (!getFallback || e.target.dataset.fallbackApplied) return;
                    e.target.dataset.fallbackApplied = 'true';
                    e.target.src = getFallback(picture, index);
                  }}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCarousel;
